import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/org-context";

type DestinationOption = {
  provider: string;
  label: string;
  configured: boolean;
};

export function CredentialHandoffPanel({
  destinations,
  canManage,
}: {
  destinations: DestinationOption[];
  canManage: boolean;
}) {
  const { orgId } = useOrg();
  const [provider, setProvider] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [handoffUrl, setHandoffUrl] = useState<string | null>(null);

  const options = useMemo(
    () => destinations.filter((d) => !d.configured),
    [destinations],
  );

  async function createLink() {
    if (!canManage || !provider || busy) return;
    setBusy(true);
    setError(null);
    setHandoffUrl(null);
    try {
      const res = await api<{ handoffUrl: string }>(
        `/api/accounts/${orgId}/credential-handoff`,
        {
          method: "POST",
          body: JSON.stringify({ provider, expiresInHours: 72 }),
        },
      );
      setHandoffUrl(res.handoffUrl);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Could not create handoff link",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!canManage || options.length === 0) return null;

  return (
    <Panel>
      <PanelHeader title="Send Destination Setup Link" />
      <div className="space-y-3 p-5">
        <p className="text-sm text-rs-ink-muted">
          Not the CRM admin? Send a one-time link so someone else can paste
          destination credentials without joining your workspace. Links expire
          in 72 hours and can be used once.
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="handoff-provider">Destination</Label>
          <select
            id="handoff-provider"
            className="h-12 w-full rounded-rs border border-rs-border bg-rs-surface px-4 text-sm text-rs-ink"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            <option value="">Select a destination…</option>
            {options.map((d) => (
              <option key={d.provider} value={d.provider}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
        {error ? <p className="text-sm text-rs-danger">{error}</p> : null}
        {handoffUrl ? (
          <div className="space-y-2">
            <Label htmlFor="handoff-url">Handoff Link</Label>
            <Input
              id="handoff-url"
              readOnly
              value={handoffUrl}
              onFocus={(e) => e.target.select()}
            />
            <p className="text-xs text-rs-ink-muted">
              Copy and send this link to your CRM admin.
            </p>
          </div>
        ) : null}
        <Button disabled={!provider || busy} onClick={() => void createLink()}>
          {busy ? "Creating…" : "Create Setup Link"}
        </Button>
      </div>
    </Panel>
  );
}
